<template>
    <Dialog
        :visible="visible"
        @update:visible="emit('update:visible', $event)"
        :header="header"
        :modal="true"
        :closable="true"
        :style="{ width: '50vw' }"
        :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
        @hide="handleClose"
    >
        <div class="space-y-4">
            <!-- Site Information Section -->
            <SiteFeaturesInfo v-if="siteFeatures" :features="siteFeatures" :accepted-anchors="computedAcceptedAnchors" />

            <!-- Content Type Selection -->
            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Content Type') }} <span class="text-red-500">*</span>
                </label>
                <div class="flex gap-4">
                    <div class="flex items-center">
                        <RadioButton
                            v-model="contentType"
                            inputId="type_link"
                            name="content_type"
                            value="link"
                            @change="onContentTypeChange"
                        />
                        <label for="type_link" class="ml-2 cursor-pointer" @click.prevent="toggleContentType('link')">
                            {{ $t('Google Docs Link') }}
                        </label>
                    </div>
                    <div v-if="!hideAutoContent" class="flex items-center">
                        <RadioButton
                            v-model="contentType"
                            inputId="type_article"
                            name="content_type"
                            value="article"
                            @change="onContentTypeChange"
                        />
                        <label for="type_article" class="ml-2 cursor-pointer" @click.prevent="toggleContentType('article')">
                            {{ $t('Request 500-word article') }}
                        </label>
                    </div>
                </div>
            </div>

            <!-- Google Docs Link (only shown when Google Docs is selected) -->
            <div v-if="contentType === 'link'">
                <label for="content_link" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Google Docs Link') }} <span class="text-red-500">*</span>
                </label>
                <div class="space-y-3">
                    <InputText
                        id="content_link"
                        v-model="contentForm.content_link"
                        class="w-full"
                        type="url"
                        :placeholder="$t('Enter Google Docs link (e.g., https://docs.google.com/document/d/...)')"
                        :invalid="!!formErrors.content_link || !!googleDocsValidationError"
                    />

                    <!-- Validation Error Message -->
                    <div v-if="googleDocsValidationError" class="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div class="flex items-start gap-2">
                            <i class="pi pi-exclamation-triangle text-red-500 text-sm mt-0.5"></i>
                            <div>
                                <p class="text-red-800 text-sm font-medium mb-1">{{ $t('Validation Error') }}</p>
                                <p class="text-red-700 text-sm" v-html="googleDocsValidationError"></p>
                            </div>
                        </div>
                    </div>
                </div>

                <small v-if="formErrors.content_link" class="text-red-500 block mt-1">
                    {{ Array.isArray(formErrors.content_link) ? formErrors.content_link[0] : formErrors.content_link }}
                </small>
                <div class="flex items-center gap-2 mt-1">
                    <small class="text-surface-500">
                        {{ $t('Paste the shareable link from Google Docs') }}
                    </small>
                    <small v-if="extractingAnchors" class="text-primary-500 flex items-center gap-1">
                        <i class="pi pi-spin pi-spinner text-xs"></i>
                        {{ $t('Reading anchors...') }}
                    </small>
                    <small v-else-if="extractedAnchors.length > 0" class="text-green-600 flex items-center gap-1">
                        <i class="pi pi-check text-xs"></i>
                        {{ extractedAnchors.length }} {{ $t('anchors found') }}
                    </small>
                    <small v-else-if="extractAnchorsError" class="text-orange-500 flex items-center gap-1">
                        <i class="pi pi-exclamation-triangle text-xs"></i>
                        {{ extractAnchorsError }}
                    </small>
                </div>
            </div>

            <div v-if="contentType === 'article' && !hideAutoContent" class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div class="flex items-start gap-3">
                    <i class="pi pi-info-circle text-blue-600 text-lg mt-0.5"></i>
                    <div>
                        <h4 class="text-blue-900 font-medium mb-1">{{ $t('Article Generation Service') }}</h4>
                        <p class="text-blue-700 text-sm leading-relaxed">
                            {{ $t('Our team will generate an article of up to 500 words with the correct anchor URL and anchor name.') }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Multiple Anchors Section -->
            <div>
                <label class="block text-sm font-medium text-surface-700 mb-3">
                    {{ $t('Anchors') }} <span class="text-red-500">*</span>
                    <span class="text-xs text-surface-500 ml-1">({{ $t('At least one is required') }})</span>
                </label>

                <div class="space-y-4">
                    <div
                        v-for="(anchor, index) in contentForm.anchors.slice(0, Math.min(visibleAnchors, computedAcceptedAnchors))"
                        :key="index"
                        class="p-4 border border-surface-200 rounded-lg bg-surface-50"
                    >
                        <div class="flex items-center justify-between mb-3">
                            <h5 class="text-sm font-semibold text-surface-700">
                                {{ $t('Anchor') }} {{ index + 1 }}
                            </h5>
                            <button
                                v-if="index > 0 && index === visibleAnchors - 1"
                                @click="removeAnchor(index)"
                                type="button"
                                class="text-red-500 hover:text-red-700 p-1 rounded"
                                :title="$t('Remove this anchor')"
                            >
                                <i class="pi pi-times text-sm"></i>
                            </button>
                        </div>

                        <!-- Quick-fill from Google Docs -->
                        <div v-if="extractedAnchors.length > 0 || extractingAnchors" class="mb-3">
                            <label class="block text-xs font-medium text-primary-600 mb-1">
                                <i class="pi pi-bolt text-xs mr-1"></i>
                                {{ $t('Quick-fill from document') }}
                            </label>
                            <Select
                                :options="extractedAnchors"
                                :optionLabel="item => item.text + '  →  ' + item.url"
                                :placeholder="extractingAnchors ? $t('Reading document...') : $t('Select an anchor to auto-fill')"
                                :loading="extractingAnchors"
                                :disabled="extractingAnchors"
                                class="w-full text-sm"
                                @change="e => applyExtractedAnchor(index, e.value)"
                                filter
                                :filterPlaceholder="$t('Search')"
                            />
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <!-- Anchor Name -->
                            <div>
                                <label :for="`anchor_name_${index}`" class="block text-xs font-medium text-surface-600 mb-1">
                                    {{ $t('Anchor Name') }}
                                </label>
                                <InputText
                                    :id="`anchor_name_${index}`"
                                    v-model="anchor.name"
                                    class="w-full"
                                    :class="{ 'border-red-500': formErrors[`anchors.${index}.name`] }"
                                    :placeholder="$t('Enter anchor text')"
                                />
                                <small v-if="formErrors[`anchors.${index}.name`]" class="text-red-500">
                                    {{ formErrors[`anchors.${index}.name`][0] }}
                                </small>
                            </div>

                            <!-- Anchor URL -->
                            <div>
                                <label :for="`anchor_url_${index}`" class="block text-xs font-medium text-surface-600 mb-1">
                                    {{ $t('Anchor URL') }}
                                </label>
                                <InputText
                                    :id="`anchor_url_${index}`"
                                    v-model="anchor.url"
                                    type="url"
                                    class="w-full"
                                    :class="{ 'border-red-500': formErrors[`anchors.${index}.url`] || anchorUrlErrors[index] }"
                                    :placeholder="$t('Enter target URL (e.g., https://example.com)')"
                                    :invalid="!!formErrors[`anchors.${index}.url`] || !!anchorUrlErrors[index]"
                                />
                                <small v-if="anchorUrlErrors[index]" class="text-red-500 block">
                                    {{ anchorUrlErrors[index] }}
                                </small>
                                <small v-else-if="formErrors[`anchors.${index}.url`]" class="text-red-500">
                                    {{ formErrors[`anchors.${index}.url`][0] }}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Add Anchor Buttons -->
                <div class="space-y-2 mt-3">
                    <div v-if="visibleAnchors === 1 && computedAcceptedAnchors >= 2" class="flex justify-start">
                        <button
                            @click="addAnchor(2)"
                            type="button"
                            class="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 border-2 border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-700 rounded-lg transition-colors duration-200 bg-transparent"
                        >
                            <i class="pi pi-plus text-sm"></i>
                            Add Anchor 2
                        </button>
                    </div>

                    <div v-if="visibleAnchors <= 2 && computedAcceptedAnchors >= 3" class="flex justify-start">
                        <button
                            @click="addAnchor(3)"
                            type="button"
                            class="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 border-2 border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-700 rounded-lg transition-colors duration-200 bg-transparent"
                        >
                            <i class="pi pi-plus text-sm"></i>
                            Add Anchor 3
                        </button>
                    </div>
                </div>
            </div>

            <div>
                <label for="language" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Language') }} <span class="text-red-500">*</span>
                </label>
                <Select
                    id="language"
                    v-model="contentForm.language"
                    :options="languages"
                    optionLabel="name"
                    optionValue="name"
                    :placeholder="$t('Select language')"
                    class="w-full"
                    :invalid="!!formErrors.language"
                    filter
                    :filterPlaceholder="$t('Search language')"
                />
                <small v-if="formErrors.language" class="text-red-500">
                    {{ Array.isArray(formErrors.language) ? formErrors.language[0] : formErrors.language }}
                </small>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2">
                <Button
                    :label="$t('Cancel')"
                    severity="secondary"
                    outlined
                    @click="handleClose"
                    :disabled="submitting || submittingInternal || validatingGoogleDocs"
                />
                <Button
                    v-if="allowSkip"
                    :label="$t('Salvar sem Validar')"
                    icon="pi pi-forward"
                    severity="warn"
                    outlined
                    @click="submitSkip"
                    :disabled="submitting || submittingInternal || validatingGoogleDocs"
                />
                <Button
                    :label="$t('Save')"
                    icon="pi pi-check"
                    @click="() => submitContent()"
                    :loading="submitting || submittingInternal || validatingGoogleDocs"
                    :disabled="submitting || submittingInternal || validatingGoogleDocs"
                />
            </div>
        </template>
    </Dialog>

    <Dialog
        v-model:visible="showSimilarityConfirmation"
        modal
        :closable="false"
        header="Confirmação de Similaridade"
        class="w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2"
    >
        <div v-if="similarityData" class="space-y-6">
            <div class="grid md:grid-cols-2 gap-4">
                <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 class="font-semibold text-red-800 mb-2">Nome do Anchor (Seu):</h4>
                    <div 
                        class="text-sm text-red-700 break-words"
                        v-html="highlightDifferences(similarityData.expected, similarityData.found).highlighted1"
                    ></div>
                </div>
                
                <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 class="font-semibold text-green-800 mb-2">Encontrado no Documento:</h4>
                    <div 
                        class="text-sm text-green-700 break-words"
                        v-html="highlightDifferences(similarityData.expected, similarityData.found).highlighted2"
                    ></div>
                </div>
            </div>
            
            <div class="text-center text-sm text-gray-600">
                <p class="text-xs">
                    <strong>Continuar:</strong> Salva com o nome atual (aceita a diferença)
                    <br>
                    <strong>Cancelar:</strong> Volta para edição
                </p>
            </div>
        </div>
        
        <template #footer>
            <div class="flex justify-center space-x-4">
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    class="p-button-secondary"
                    @click="cancelSimilarity"
                />
                <Button
                    label="Continuar Mesmo Assim"
                    icon="pi pi-check"
                    class="p-button-warning"
                    @click="confirmSimilarity"
                />
            </div>
        </template>
    </Dialog>
</template>

<script setup>
import { reactive, ref, computed, watch, getCurrentInstance } from 'vue';
import { useToast } from 'primevue/usetoast';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Select from 'primevue/select';
import RadioButton from 'primevue/radiobutton';
import axios from 'axios';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

// Props
const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    header: {
        type: String,
        default: 'Add Content'
    },
    siteFeatures: {
        type: Object,
        default: null
    },
    maxAnchors: {
        type: Number,
        default: 1
    },
    acceptedAnchors: {
        type: Number,
        default: null
    },
    languages: {
        type: Array,
        default: () => []
    },
    submitting: {
        type: Boolean,
        default: false
    },
    selectedOrderItem: {
        type: Object,
        default: null
    },
    initialData: {
        type: Object,
        default: () => ({})
    },
    hideAutoContent: {
        type: Boolean,
        default: false
    },
    allowSkip: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:visible', 'submit', 'close']);

const contentForm = reactive({
    anchors: [{ name: '', url: '' }], 
    language: '',
    content_link: '',
    auto_content: null,
    reset: function() {
        this.anchors = [{ name: '', url: '' }];
        this.language = '';
        this.content_link = '';
        this.auto_content = null;
    }
});

const formErrors = ref({});
const contentType = ref(null);
const visibleAnchors = ref(1);
const googleDocsValidationError = ref('');
const anchorUrlErrors = ref({});
const validatingGoogleDocs = ref(false);
const submittingInternal = ref(false);
const showSimilarityConfirmation = ref(false);
const similarityData = ref(null);

const extractedAnchors = ref([]);
const extractingAnchors = ref(false);
const extractAnchorsError = ref('');
let extractDebounceTimer = null;

const validateAnchorUrl = (url) => {
    if (!url || url.trim() === '') {
        return '';
    }
    
    try {
        new URL(url);
        return '';
    } catch (e) {
        return $t('Please enter a valid URL (e.g., https://example.com)');
    }
};

watch(() => props.hideAutoContent, (newValue) => {
    if (newValue && !contentType.value) {
        contentType.value = 'link';
        contentForm.auto_content = 1;
    }
}, { immediate: true });

const maxAnchorsComputed = computed(() => {
    const anchorsValue = props.selectedOrderItem?.accepted_anchors || props.acceptedAnchors || props.maxAnchors;
    return Math.min(anchorsValue, 3);
});

const computedAcceptedAnchors = computed(() => {
    let acceptedAnchors = 1;

    if (props.selectedOrderItem?.accepted_anchors && props.selectedOrderItem.accepted_anchors > 0) {
        acceptedAnchors = props.selectedOrderItem.accepted_anchors;
    }
    else if (props.maxAnchors && props.maxAnchors > 0) {
        acceptedAnchors = props.maxAnchors;
    }
    else if (props.acceptedAnchors && props.acceptedAnchors > 0) {
        acceptedAnchors = props.acceptedAnchors;
    }
    
    return Math.max(acceptedAnchors, 1);
});

const addAnchor = (targetAnchor = null) => {
    const maxAnchors = computedAcceptedAnchors.value;
    if (targetAnchor) {
        visibleAnchors.value = Math.min(targetAnchor, maxAnchors);
    } else {
        if (visibleAnchors.value < maxAnchors) {
            visibleAnchors.value++;
        }
    }
};

const removeAnchor = (index) => {
    if (visibleAnchors.value > 1 && index === visibleAnchors.value - 1) {
        contentForm.anchors[index].name = '';
        contentForm.anchors[index].url = '';
        visibleAnchors.value--;
    }
};

watch(() => contentForm.auto_content, (newValue) => {
    if (newValue === 1 && !props.hideAutoContent) {
        contentForm.content_link = '';
    }
});

const onContentTypeChange = () => {
    if (contentType.value === 'link') {
        contentForm.auto_content = props.hideAutoContent ? 1 : 0;
    } else if (contentType.value === 'article') {
        contentForm.auto_content = 1;
        contentForm.content_link = '';
    } else {
        contentForm.auto_content = null;
    }
};

const toggleContentType = (value) => {
    if (contentType.value === value) {
        contentType.value = null;
        contentForm.auto_content = props.hideAutoContent ? 1 : null;
        contentForm.content_link = '';
        googleDocsValidationError.value = '';
    } else {
        contentType.value = value;
        onContentTypeChange();
        googleDocsValidationError.value = '';
    }
};

const validateGoogleDocsContent = async () => {
    if (!contentForm.content_link) {
        return true; 
    }
    
    try {
        validatingGoogleDocs.value = true;
        googleDocsValidationError.value = '';
        
        const response = await axios.post('/google-docs/validate-content', {
            google_docs_url: contentForm.content_link,
            anchor_name: contentForm.anchors[0]?.name?.trim() || null,
            anchor_url: contentForm.anchors[0]?.url?.trim() || null,
            anchor_name_2: contentForm.anchors[1]?.name?.trim() || null,
            anchor_url_2: contentForm.anchors[1]?.url?.trim() || null,
            anchor_name_3: contentForm.anchors[2]?.name?.trim() || null,
            anchor_url_3: contentForm.anchors[2]?.url?.trim() || null,
        });
        
        if (response.data.valid === true) {
            return true;
        } else if (response.data.valid === 'similar') {
            similarityData.value = {
                similarity: response.data.similarity,
                expected: response.data.expected,
                found: response.data.found,
                url: response.data.url,
                position: response.data.position || 1
            };
            showSimilarityConfirmation.value = true;
            return false;
        } else {
            const errors = response.data.errors || [];
            if (errors.length > 0) {
                googleDocsValidationError.value = errors.join('<br>');
            } else {
                googleDocsValidationError.value = response.data.error || 'Unknown validation error';
            }
            throw new Error('validation_failed');
        }
    } catch (error) {
        if (error.message === 'validation_failed') {
            throw error;
        } else {
            if (error.response) {
                const status = error.response.status;
                const data = error.response.data;
                
                if (status === 404) {
                    googleDocsValidationError.value = 'Validation service not found. Please contact support.';
                } else if (status === 422) {
                    googleDocsValidationError.value = data.message || 'Invalid request data.';
                } else if (status === 500) {
                    googleDocsValidationError.value = data.error || 'Server error occurred during validation.';
                } else {
                    googleDocsValidationError.value = `Error ${status}: ${data.error || data.message || 'Unknown error'}`;
                }
            } else if (error.request) {
                googleDocsValidationError.value = 'No response from validation service. Please check your internet connection.';
            } else {
                googleDocsValidationError.value = 'Error setting up validation request. Please try again.';
            }
            throw error;
        }
    } finally {
        validatingGoogleDocs.value = false;
    }
};

watch(() => contentForm.content_link, (url) => {
    googleDocsValidationError.value = '';
    extractedAnchors.value = [];
    extractAnchorsError.value = '';

    clearTimeout(extractDebounceTimer);

    if (!url || !isGoogleDocsUrl(url)) return;

    extractDebounceTimer = setTimeout(() => fetchExtractedAnchors(url), 600);
});

const isGoogleDocsUrl = (url) => {
    return /docs\.google\.com\/document\/d\//.test(url);
};

const fetchExtractedAnchors = async (url) => {
    extractingAnchors.value = true;
    extractAnchorsError.value = '';
    extractedAnchors.value = [];

    try {
        const response = await axios.post('/google-docs/extract-anchors', { google_docs_url: url });
        extractedAnchors.value = response.data.anchors || [];
        if (extractedAnchors.value.length === 0) {
            extractAnchorsError.value = $t('No anchors found in the document');
        }
    } catch (e) {
        extractAnchorsError.value = e.response?.data?.error || $t('Could not read the document');
    } finally {
        extractingAnchors.value = false;
    }
};

const applyExtractedAnchor = (index, selected) => {
    if (!selected) return;
    contentForm.anchors[index].name = selected.text;
    contentForm.anchors[index].url = selected.url;
};

// Watch for anchor URL changes and validate them
watch(() => contentForm.anchors, (newAnchors) => {
    googleDocsValidationError.value = '';

    newAnchors.forEach((anchor, index) => {
        if (anchor.url) {
            const error = validateAnchorUrl(anchor.url);
            if (error) {
                anchorUrlErrors.value[index] = error;
            } else {
                delete anchorUrlErrors.value[index];
            }
        } else {
            delete anchorUrlErrors.value[index];
        }
    });
}, { deep: true });
 
watch(() => contentForm.anchors, () => {
    googleDocsValidationError.value = '';
}, { deep: true });

const submitContent = async (forceContinue = false) => {
    if (forceContinue && typeof forceContinue === 'object' && forceContinue.type) {
        forceContinue = false; 
    }
    
    if (!props.selectedOrderItem) return;

    const hasValidAnchor = contentForm.anchors.some(anchor => anchor.name.trim() && anchor.url.trim());
    const hasIncompleteAnchor = contentForm.anchors.some(anchor => {
        const hasName = anchor.name.trim().length > 0;
        const hasUrl = anchor.url.trim().length > 0;
        return (hasName && !hasUrl) || (!hasName && hasUrl);
    });
    
    if (!hasValidAnchor || !contentForm.language) {
        toast.add({
            severity: 'warn',
            summary: $t('Validation Error'),
            detail: $t('Please fill in at least one anchor name and URL, and select a language'),
            life: 3000
        });
        return;
    }
    
    if (hasIncompleteAnchor) {
        toast.add({
            severity: 'error',
            summary: $t('Incomplete Anchors'),
            detail: $t('Anchor fields must be either completely filled (both name and URL) or completely empty. Please complete the incomplete anchors or clear them.'),
            life: 5000
        });
        return;
    }

    if (!contentType.value) {
        toast.add({
            severity: 'warn',
            summary: $t('Validation Error'),
            detail: $t('Please select a content type'),
            life: 3000
        });
        return;
    }
    
    if (contentType.value === 'link' && !contentForm.content_link) {
        toast.add({
            severity: 'warn',
            summary: $t('Validation Error'),
            detail: $t('Please provide a Google Docs link'),
            life: 3000
        });
        return;
    }

    if (Object.keys(anchorUrlErrors.value).length > 0) {
        toast.add({
            severity: 'warn',
            summary: $t('Invalid Anchor URLs'),
            detail: $t('Please fix the invalid URLs in your anchors'),
            life: 3000
        });
        return;
    }

    if (contentType.value === 'link' && contentForm.content_link && !forceContinue) {
        submittingInternal.value = true;
        
        try {
            const validationResult = await validateGoogleDocsContent();

            if (validationResult === false) {
                submittingInternal.value = false;
                return;
            }

            if (googleDocsValidationError.value) {
                submittingInternal.value = false;
                return;
            }

        } catch (error) {
            submittingInternal.value = false;
            toast.add({
                severity: 'error',
                summary: $t('Validation Error'),
                detail: $t('Error occurred while validating Google Docs content'),
                life: 3000
            });
            return;
        }
    }

    if (!submittingInternal.value) {
        submittingInternal.value = true;
    }

    const formData = {
        anchor_name: contentForm.anchors[0]?.name?.trim() || null,
        anchor_url: contentForm.anchors[0]?.url?.trim() || null,
        anchor_name_2: contentForm.anchors[1]?.name?.trim() || null,
        anchor_url_2: contentForm.anchors[1]?.url?.trim() || null,
        anchor_name_3: contentForm.anchors[2]?.name?.trim() || null,
        anchor_url_3: contentForm.anchors[2]?.url?.trim() || null,
        language: contentForm.language,
        content_link: contentForm.content_link,
        auto_content: contentForm.auto_content,
    };

    if (forceContinue) {
        formData.force_continue = true;
    }

    emit('submit', formData);
    submittingInternal.value = false;
};

const submitSkip = () => {
    const formData = {
        anchor_name: contentForm.anchors[0]?.name?.trim() || null,
        anchor_url: contentForm.anchors[0]?.url?.trim() || null,
        anchor_name_2: contentForm.anchors[1]?.name?.trim() || null,
        anchor_url_2: contentForm.anchors[1]?.url?.trim() || null,
        anchor_name_3: contentForm.anchors[2]?.name?.trim() || null,
        anchor_url_3: contentForm.anchors[2]?.url?.trim() || null,
        language: contentForm.language || null,
        content_link: contentForm.content_link || null,
        auto_content: contentForm.auto_content,
        skipped: true,
    };
    emit('submit', formData);
};

const confirmSimilarity = () => {
    showSimilarityConfirmation.value = false;
    similarityData.value = null;
    submitContent(true); 
};

const cancelSimilarity = () => {
    showSimilarityConfirmation.value = false;
    similarityData.value = null;
    submittingInternal.value = false;
};

const highlightDifferences = (text1, text2) => {
    const words1 = text1.split(' ');
    const words2 = text2.split(' ');
    const maxLength = Math.max(words1.length, words2.length);
    
    let highlighted1 = '';
    let highlighted2 = '';
    
    for (let i = 0; i < maxLength; i++) {
        const word1 = words1[i] || '';
        const word2 = words2[i] || '';
        
        if (word1 !== word2) {
            highlighted1 += `<span class="bg-red-200 text-red-800 px-1 rounded">${word1}</span> `;
            highlighted2 += `<span class="bg-green-200 text-green-800 px-1 rounded">${word2}</span> `;
        } else {
            highlighted1 += word1 + ' ';
            highlighted2 += word2 + ' ';
        }
    }
    
    return {
        highlighted1: highlighted1.trim(),
        highlighted2: highlighted2.trim()
    };
};

const handleClose = () => {
    emit('update:visible', false);
    emit('close');
    resetForm();
};

const handleSubmit = () => {
    submitContent(false);
};

const resetForm = () => {
    contentForm.reset();
    contentType.value = null;
    visibleAnchors.value = 1;
    formErrors.value = {};
    googleDocsValidationError.value = '';
    anchorUrlErrors.value = {};
    validatingGoogleDocs.value = false;
    submittingInternal.value = false;
    showSimilarityConfirmation.value = false;
    similarityData.value = null;
    extractedAnchors.value = [];
    extractingAnchors.value = false;
    extractAnchorsError.value = '';
    clearTimeout(extractDebounceTimer);
};

watch(() => props.visible, (newValue) => {
    if (newValue) {
        resetForm();

        const initialAnchors = Math.min(computedAcceptedAnchors.value, 3);
        contentForm.anchors = Array(initialAnchors).fill().map(() => ({ name: '', url: '' }));

        if (props.languages && props.languages.length > 0) {
            contentForm.language = props.languages.find(lang => lang.name === 'English')?.name || props.languages[0]?.name || '';
        } else {
            contentForm.language = 'English';
        }
        
        if (props.initialData && Object.keys(props.initialData).length > 0) {
            const data = props.initialData;
            
            if (data.anchor_name || data.anchor_url) {
                contentForm.anchors[0] = {
                    name: data.anchor_name || '',
                    url: data.anchor_url || ''
                };
            }
            
            if (data.anchor_name_2 || data.anchor_url_2) {
                contentForm.anchors[1] = {
                    name: data.anchor_name_2 || '',
                    url: data.anchor_url_2 || ''
                };
            }
            
            if (data.anchor_name_3 || data.anchor_url_3) {
                contentForm.anchors[2] = {
                    name: data.anchor_name_3 || '',
                    url: data.anchor_url_3 || ''
                };
            }
            
            if (data.language) {
                contentForm.language = data.language;
            }
            
            if (data.content_link) {
                contentForm.content_link = data.content_link;
            }
            
            if (data.auto_content !== undefined) {
                contentForm.auto_content = data.auto_content;
                if (data.auto_content === 0) {
                    contentType.value = 'link';
                } else if (data.auto_content === 1) {
                    contentType.value = 'article';
                }
            }
            
            let filledAnchors = 1;
            if (data.anchor_name_2 || data.anchor_url_2) filledAnchors = Math.max(filledAnchors, 2);
            if (data.anchor_name_3 || data.anchor_url_3) filledAnchors = Math.max(filledAnchors, 3);
            visibleAnchors.value = Math.min(filledAnchors, computedAcceptedAnchors.value);
        }
    }
});

watch(() => props.submitting, (newValue) => {
    if (!newValue) {
        validatingGoogleDocs.value = false;
    }
});
</script>